import { computed, reactive } from 'vue';
import { useQuery } from 'vue-query';
import { QueryObserverOptions } from 'react-query/core';
import useTokens from '@/composables/useTokens';
import QUERY_KEYS from '@/constants/queryKeys';
import { formatUnits } from 'ethers/lib/utils';
import { getAddress, isAddress } from '@ethersproject/address';
import { balancerContractsService } from '@/services/balancer/contracts/balancer-contracts.service';
import { balancerSubgraphService } from '@/services/balancer/subgraph/balancer-subgraph.service';
import { FullPool, LinearPool, Pool } from '@/services/balancer/subgraph/types';
import { POOLS } from '@/constants/pools';
import useApp from '../useApp';
import useUserSettings from '../useUserSettings';
import useWeb3 from '@/services/web3/useWeb3';
import { bnum, forChange } from '@/lib/utils';
import {
  lpTokensFor,
  isManaged,
  isStableLike,
  isStablePhantom,
  isWeightedBoosted,
  isMetaStableBoosted
} from '../usePool';

export default function usePoolQuery(
  id: string,
  options: QueryObserverOptions<FullPool> = {}
) {
  /**
   * COMPOSABLES
   */
  const { getTokens, injectTokens, prices, dynamicDataLoading } = useTokens();
  const { appLoading } = useApp();
  const { account } = useWeb3();
  const { currency } = useUserSettings();

  /**
   * COMPUTED
   */
  const enabled = computed(
    () => !appLoading.value && !dynamicDataLoading.value
  );

  /**
   * METHODS
   */
  function isBlocked(pool: Pool): boolean {
    const requiresAllowlisting =
      isStableLike(pool.poolType) || isManaged(pool.poolType);
    const isOwnedByUser =
      isAddress(account.value) &&
      isAddress(pool.owner) &&
      getAddress(pool.owner) === getAddress(account.value);
    const isAllowlisted =
      POOLS.Stable.AllowList.includes(id) ||
      POOLS.Investment.AllowList.includes(id);

    console.log(
      'isBlocked',
      requiresAllowlisting && !isAllowlisted && !isOwnedByUser
      //  account.value, typeof (account.value), pool.owner, pool
    );

    return requiresAllowlisting && !isAllowlisted && !isOwnedByUser;
  }

  function removePreMintedBPT(pool: Pool): Pool {
    const poolAddress = balancerSubgraphService.pools.addressFor(pool.id);
    // Remove pre-minted BPT token if exits
    pool.tokensList = pool.tokensList.filter(
      address => address !== poolAddress.toLowerCase()
    );
    return pool;
  }

  /**
   * fetches StablePhantom linear pools and extracts
   * required attributes.
   */
  async function getLinearPoolAttrs(pool: Pool): Promise<Pool> {
    // Fetch linear pools from subgraph
    const linearPools = (await balancerSubgraphService.pools.get(
      {
        where: {
          address_in: pool.tokensList,
          totalShares_gt: -1 // Avoid the filtering for low liquidity pools
        }
      },
      { mainIndex: true, wrappedIndex: true }
    )) as LinearPool[];

    const linearPoolTokensMap: Pool['linearPoolTokensMap'] = {};

    // Inject main/wrapped tokens into pool schema
    linearPools.forEach(linearPool => {
      if (!pool.mainTokens) pool.mainTokens = [];
      if (!pool.wrappedTokens) pool.wrappedTokens = [];

      const index = pool.tokensList.indexOf(linearPool.address.toLowerCase());

      pool.mainTokens[index] = getAddress(
        linearPool.tokensList[linearPool.mainIndex]
      );
      pool.wrappedTokens[index] = getAddress(
        linearPool.tokensList[linearPool.wrappedIndex]
      );

      linearPool.tokens
        .filter(token => token.address !== linearPool.address)
        .forEach(token => {
          const address = getAddress(token.address);

          linearPoolTokensMap[address] = {
            ...token,
            address
          };
        });
    });

    pool.linearPoolTokensMap = linearPoolTokensMap;

    return pool;
  }

  /**
   * fetches Weighted Boosted pools and extracts
   * required attributes.
   */
  async function getBoostedPoolAttrs(pool: Pool): Promise<Pool> {
    // Fetch linear pools from subgraph
    const linearPools = (await balancerSubgraphService.pools.get(
      {
        where: {
          address_in: pool.tokensList,
          totalShares_gt: -1 // Avoid the filtering for low liquidity pools
        }
      },
      { mainIndex: true, wrappedIndex: true }
    )) as LinearPool[];

    if (linearPools.length !== 2) {
      return pool;
    }

    const pbtPoolIndex = POOLS.PhantomStable.includes(
      linearPools[0].address.toLowerCase()
    )
      ? 0
      : 1;
    const linearPoolIndex = POOLS.PhantomStable.includes(
      linearPools[0].address.toLowerCase()
    )
      ? 1
      : 0;

    const linearPoolTokensMap: Pool['linearPoolTokensMap'] = {};

    // // Inject main/wrapped tokens into pool schema
    linearPools[linearPoolIndex].tokens
      // .filter(token => token.address !== linearPools[linearPoolIndex].address)
      .forEach(token => {
        const address = getAddress(token.address);

        linearPoolTokensMap[address] = {
          ...token,
          address
        };
      });

    const pbtLinearPools = (await balancerSubgraphService.pools.get(
      {
        where: {
          address_in: linearPools[pbtPoolIndex].tokensList,
          totalShares_gt: -1 // Avoid the filtering for low liquidity pools
        }
      },
      { mainIndex: true, wrappedIndex: true }
    )) as LinearPool[];

    // Inject main/wrapped tokens into pool schema
    pbtLinearPools.forEach(linearPool => {
      linearPool.tokens
        // .filter(token => token.address !== linearPool.address)
        .forEach(token => {
          const address = getAddress(token.address);

          linearPoolTokensMap[address] = {
            ...token,
            address
          };
        });
    });

    pool.linearPoolTokensMap = linearPoolTokensMap;

    pool.tokensList = [
      ...linearPools[pbtPoolIndex].tokensList,
      linearPools[linearPoolIndex].address
    ];
    pool.tokensList = pool.tokensList.filter(
      v => v.toLowerCase() !== linearPools[pbtPoolIndex].address.toLowerCase()
    );

    return pool;
  }

  async function getMetaStableBoostedPoolInfo(pool: Pool): Promise<Pool> {
    const parents = pool.tokensList;
    const subs = parents.map(v => {
      return getAddress(POOLS.MetaStableBoostedTokens[v.toLowerCase()][0]);
    });
    pool.tokensList = subs;
    pool.tokens[0].address = subs[0];
    pool.tokens[1].address = subs[1];
    pool.isMetaBoosted = true;
    pool.metaBoostedParentTokens = parents;
    return pool;
  }

  /**
   * QUERY INPUTS
   */
  const queryKey = QUERY_KEYS.Pools.Current(id);

  const queryFn = async () => {
    let [pool] = await balancerSubgraphService.pools.get({
      where: {
        id: id.toLowerCase(),
        totalShares_gt: -1, // Avoid the filtering for low liquidity pools
        poolType_not_in: POOLS.ExcludedPoolTypes
      }
    });

    if (isBlocked(pool)) throw new Error('Pool not allowed');

    const isStablePhantomPool = isStablePhantom(pool.poolType);
    const isWeightedBoostedPool = isWeightedBoosted(pool);
    const isMetaStableBoostedPool = isMetaStableBoosted(pool);

    console.log('!!! rawPool', pool);

    if (isStablePhantomPool) {
      pool = removePreMintedBPT(pool);
      pool = await getLinearPoolAttrs(pool);
    } else if (isWeightedBoostedPool) {
      try {
        pool = await getBoostedPoolAttrs(pool);
      } catch (error) {
        console.error('!!! pool1', error);
      }
    } else if (isMetaStableBoostedPool) {
      pool = await getMetaStableBoostedPoolInfo(pool);
    }

    console.log('!!! rawPool_1', pool);

    if (pool.isMetaBoosted && pool.metaBoostedParentTokens) {
      // Inject relevant pool tokens to fetch metadata
      await injectTokens([
        ...pool.tokensList,
        ...lpTokensFor(pool),
        balancerSubgraphService.pools.addressFor(pool.id),
        ...pool.metaBoostedParentTokens
      ]);
    } else {
      // Inject relevant pool tokens to fetch metadata
      await injectTokens([
        ...pool.tokensList,
        ...lpTokensFor(pool),
        balancerSubgraphService.pools.addressFor(pool.id)
      ]);
    }

    await forChange(dynamicDataLoading, false);

    // Need to fetch onchain pool data first so that calculations can be
    // performed in the decoration step.
    const poolTokenMeta = getTokens(
      pool.tokensList.map(address => getAddress(address))
    );

    const onchainData = await balancerContractsService.vault.getPoolData(
      id,
      pool.poolType,
      poolTokenMeta,
      pool
    );

    console.log('!!! onchainData', onchainData, poolTokenMeta);

    if (!!pool && isWeightedBoosted(pool) && onchainData.linearPools) {
      if (!pool.mainTokens) {
        pool.mainTokens = [];
      }
      Object.keys(onchainData.linearPools).map((addr, i) => {
        if (pool.mainTokens && onchainData.linearPools) {
          pool.mainTokens[i] = getAddress(
            onchainData.linearPools[addr].mainToken.address
          );
        }
      });
    }
    console.log('!!! pool_totalLiquidity_2', pool, onchainData);

    const [decoratedPool] = await balancerSubgraphService.pools.decorate(
      [{ ...pool, onchain: onchainData }],
      '24h',
      prices.value,
      currency.value
    );

    console.log('!!! pool_totalLiquidity_1', decoratedPool.totalLiquidity);

    let unwrappedTokens: Pool['unwrappedTokens'];

    if (isStablePhantomPool && onchainData.linearPools != null) {
      unwrappedTokens = Object.entries(onchainData.linearPools).map(
        ([, linearPool]) => linearPool.unwrappedTokenAddress
      );

      if (decoratedPool.linearPoolTokensMap != null) {
        let totalLiquidity = bnum(0);
        const tokensMap = getTokens(
          Object.keys(decoratedPool.linearPoolTokensMap)
        );

        Object.entries(onchainData.linearPools).forEach(([address, token]) => {
          const tokenShare = bnum(onchainData.tokens[address].balance).div(
            token.totalSupply
          );

          const mainTokenBalance = formatUnits(
            token.mainToken.balance,
            tokensMap[token.mainToken.address].decimals
          );

          const wrappedTokenBalance = formatUnits(
            token.wrappedToken.balance,
            tokensMap[token.wrappedToken.address].decimals
          );

          const mainTokenPrice =
            prices.value[token.mainToken.address] != null
              ? prices.value[token.mainToken.address].usd
              : null;

          if (mainTokenPrice != null) {
            const mainTokenValue = bnum(mainTokenBalance)
              .times(tokenShare)
              .times(mainTokenPrice);

            const wrappedTokenValue = bnum(wrappedTokenBalance)
              .times(tokenShare)
              .times(mainTokenPrice)
              .times(token.wrappedToken.priceRate);

            totalLiquidity = bnum(totalLiquidity)
              .plus(mainTokenValue)
              .plus(wrappedTokenValue);
          }
        });

        decoratedPool.totalLiquidity = totalLiquidity.toString();
      }
    }

    console.log('!!! pool_totalLiquidity_0', decoratedPool.totalLiquidity);

    const ret = { onchain: onchainData, unwrappedTokens, ...decoratedPool };

    return ret;
  };

  const queryOptions = reactive({
    enabled,
    ...options
  });

  return useQuery<FullPool>(queryKey, queryFn, queryOptions);
}
